import useMonthlyInvoices from "@/hooks/Invoices/useMonthlyInvoices";
import { Button } from "@/shared";
import MainLayout from "@/shared/components/MainLayout";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import Header from "@/shared/components/ui/Header";
import Loader from "@/shared/components/ui/Loader";
import { motion } from 'framer-motion';
import { ListOrdered } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InvoiceFilterDialog from "./InvoiceFilterDialog";
import InvoiceTotalsDialog from "./InvoiceTotalsDialog";

const MonthlyInvoices = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        handleQuickSelect,
        date,
        setDate,
        enddate,
        setEndDate,
        dialog,
        setDialog,
        totalsByType,
        onCloseDialog,
        viewTotals,
        setViewTotals,
        loading,
        filteredInvoices,
        q,
        setAppliedInvoiceType
    } = useMonthlyInvoices();
    return (
        <MainLayout>
            <Header title={`${t('monthly invoices')}`} info={q} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <div className="w-full max-w-6xl">
                    <div className="mb-6 w-full max-w-6xl">
                        <div className="flex flex-col justify-between xl:flex-row xl:items-end items-stretch gap-4 w-full">
                            <div className="grid grid-cols-2 gap-2 md:gap-4">
                                <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("start_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today', 'from')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday', 'from')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month', 'from')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month', 'from')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker"
                                        label=''
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setDate(currentDateString)}
                                        defaultDate={date}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("end_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today', 'to')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday', 'to')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month', 'to')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month', 'to')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker1"
                                        label=""
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setEndDate(currentDateString)}
                                        defaultDate={enddate}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 xl:flex gap-2 xl:items-end">
                                <Button
                                    onClick={() => setDialog((prev) => ({ ...prev, open: true }))}
                                    className="w-full xl:w-auto bg-slate-800 text-white flex items-center justify-center gap-2 text-[12px] px-2"
                                >
                                    <ListOrdered size={16} />
                                    <span className="truncate">{t("filter_options")}</span>
                                </Button>

                                <Button
                                    className="w-full xl:w-auto text-[12px] px-2"
                                    onClick={() => setViewTotals(!viewTotals)}
                                >
                                    <span className="truncate">{t("btn_total_by_type")}</span>
                                </Button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader />
                        </div>
                    ) : (
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">

                            <div className="overflow-x-auto">
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-400 uppercase text-[11px] font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("accountid")}</th>
                                            <th className="px-6 py-4">{t("accountstring")}</th>
                                            <th className="px-6 py-4">{t("invoicedate")}</th>
                                            <th className="px-6 py-4">{t("invoiceid")}</th>
                                            <th className="px-6 py-4">{t("invoicetype")}</th>
                                            <th className="px-6 py-4">{t("totvalue")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredInvoices.length > 0 ? (
                                            filteredInvoices.map((item: any, index: number) => {
                                                const isInitialBalance = item?.invoice_type?.toLowerCase() === "old";
                                                return (
                                                    <motion.tr
                                                        key={index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: index * 0.03 }}
                                                        className={`hover:bg-violet-50/30 transition-colors group ${isInitialBalance ? "bg-slate-50 font-bold text-violet-700" : ""}`}
                                                    >
                                                        <td className="px-6 py-4 text-sm font-medium text-slate-700">
                                                            {item?.accountid || "---"}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-600">
                                                            {item?.accountstring || "---"}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-slate-500">
                                                            {item?.invoicedate || "---"}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span
                                                                onClick={() => {
                                                                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                                                    navigate(`/invoice_details/${item?.invoiceid}`)
                                                                }}
                                                                className="bg-slate-100 text-slate-700 px-2 py-1 rounded shadow-sm border border-slate-200 cursor-pointer">
                                                                {item?.invoiceid}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${isInitialBalance ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                {item?.invoicetype || "N/A"}
                                                            </span>
                                                        </td>

                                                        <td className="px-6 py-4 text-sm text-slate-900">
                                                            {Number(item?.totvalue || 0).toFixed(2)}
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-20 text-center">
                                                    <div className="flex flex-col items-center gap-3 opacity-20">
                                                        <ListOrdered size={48} />
                                                        <p className="font-black uppercase tracking-widest text-sm">{t("No Data Found")}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <InvoiceFilterDialog
                dialog={dialog}
                setDialog={setDialog}
                onCloseDialog={onCloseDialog}
                setAppliedInvoiceType={setAppliedInvoiceType}
            />
            <InvoiceTotalsDialog
                totalsByType={totalsByType}
                viewTotals={viewTotals}
                setViewTotals={setViewTotals} />

        </MainLayout >
    );
}

export default MonthlyInvoices;